/**
 * The current screen, kept in the address bar.
 *
 * Destination and sub-tab used to live in `useState`, so a refresh — or a
 * deploy reloading the page under you — dropped you back on Skida PRO → Sales
 * CRM wherever you were.
 *
 * ## Why the URL rather than localStorage
 *
 * localStorage is the smaller change and the wrong one: it fixes refresh and
 * nothing else. The URL fixes refresh *and* three things that would otherwise
 * be asked for separately — the back button stops leaving the app, a screen can
 * be linked to, and two tabs can sit on different screens instead of fighting
 * over one key.
 *
 * It is also the honest model. The app already has navigation state; it was
 * simply keeping it somewhere the browser could not see.
 *
 * ## Why no router
 *
 * `navigation.ts` is already a declarative map of every screen, which is most of
 * what a router would give us. Sixty lines here beat a dependency for one
 * feature. That trade flips the moment we want route guards or lazily loaded
 * screens — at which point this is small enough to delete.
 *
 * ## What is deliberately not in the URL
 *
 * Search text, filters, column choice and Table/Kanban. Those describe how one
 * person is working right now, not where they are. Column choice is already
 * persisted per browser, which is the right place for it: a shared link should
 * open the reader's own column set, not the sender's.
 *
 * The selected record (`/pro/crm/thredbo`) is the level that makes a link worth
 * sending, and is not built yet. `pathOf`/`screenOf` are the only places that
 * would need to change.
 */
import { useCallback, useEffect, useState } from 'react'
import { DEFAULT_SCREEN, DESTINATIONS, MOVED_SCREENS, type Screen } from './navigation'

/**
 * `pro.crm` ↔ `/pro/crm`. The dot in the `Screen` union is the separator, so
 * the mapping needs no table of its own — a screen added to `navigation.ts`
 * gets a URL without anything here changing.
 */
function pathOf(screen: Screen): string {
  return `/${screen.split('.').join('/')}`
}

const ALL_SCREENS: Screen[] = [
  ...DESTINATIONS.flatMap((d) => d.tabs.map((t) => t.key)),
  // Reachable, but not a nav item — so it is not in DESTINATIONS.
  'admin',
]

/** `/pro/newsletter` → `pro.newsletter`, the key `MOVED_SCREENS` is written in. */
function keyOf(pathname: string): string {
  return pathname.replace(/^\/+|\/+$/g, '').split('/').join('.')
}

/** The screen a path names, or null when it names nothing we have. */
function screenOf(pathname: string): Screen | null {
  const cleaned = pathname.replace(/^\/+|\/+$/g, '')
  if (!cleaned) return null
  const moved = MOVED_SCREENS[keyOf(pathname)]
  if (moved) return moved.screen
  return ALL_SCREENS.find((s) => pathOf(s) === `/${cleaned}`) ?? null
}

/**
 * Which product a moved URL was about, for the screen it landed on.
 *
 * `/pro/newsletter` stopped existing when the newsletter moved into Marketing.
 * Sending its visitor to the newsletter is half the job — sending them to the
 * *PRO* newsletter is the rest, and the difference is which of two sending
 * identities they are looking at. Read once, to seed the initial state.
 */
export function productFromUrl(): 'app' | 'pro' | null {
  return MOVED_SCREENS[keyOf(window.location.pathname)]?.product ?? null
}

export function useUrlScreen(): [Screen, (next: Screen) => void] {
  const [screen, setScreen] = useState<Screen>(
    () => screenOf(window.location.pathname) ?? DEFAULT_SCREEN,
  )

  // Anything that is not already the canonical path for the screen we resolved
  // gets rewritten — a bare "/", a typo, and now a URL that moved. The test is
  // "does the bar say what we are showing", not "was this path recognised":
  // /pro/newsletter resolves fine and would otherwise sit in the address bar
  // naming a screen that no longer exists.
  //
  // `replace`, not `push` — a URL we just corrected is not a step in the
  // history worth going back to.
  useEffect(() => {
    if (window.location.pathname !== pathOf(screen)) {
      window.history.replaceState(null, '', pathOf(screen))
    }
    // Once, on mount. Later corrections are the job of `go`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The back and forward buttons. Without this they would change the URL and
  // leave the screen behind — worse than not being in the URL at all.
  useEffect(() => {
    const onPop = () => setScreen(screenOf(window.location.pathname) ?? DEFAULT_SCREEN)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const go = useCallback((next: Screen) => {
    setScreen((current) => {
      // Guard against pushing a duplicate entry when somebody clicks the tab
      // they are already on — otherwise back appears to do nothing.
      if (next !== current) window.history.pushState(null, '', pathOf(next))
      return next
    })
  }, [])

  return [screen, go]
}
